import moment from 'moment'
import { CLEARING_TEXT } from '../utils'

// Everything this page can filter or show is declared HERE, once. V1 columns and filters are
// expected to change, so the page, the search bar and the table all read from this file rather
// than each carrying their own copy.
//
// Placed under pages/Tranzila/ so the existing Tranzila display helpers (statusText,
// operationType, formatAmount, clip-style tooltips) are a relative '../utils' import, exactly
// how Tranzila/SearchBar and Tranzila/List already reach them.

// One side of a transaction's documents, as returned by GET /tranzila/documents.
// The side is present whenever the TRANSACTION REFERENCES a document (txnfdid / cancelfdid).
// `resolved` says whether the documents-API metadata has been fetched yet; `retrieval_key`
// governs PDF availability and nothing else. A referenced-but-unresolved document is a real
// document with every metadata field still null.
export interface TranzilaDocumentSide {
    reference_id: string
    id: string | null
    number: string | null
    action: string | null
    created_at: string | null
    amount: number | null
    retrieval_key: string | null
    resolved: boolean
}

export interface TranzilaDocumentRow {
    id: string
    terminal_name: string
    child_terminal: string | null
    transaction_index: string
    transaction_date: string | null
    transaction_time: string | null
    user_id: number | null
    client_name: string | null
    user_defined_2: string | null
    email: string | null
    sku: string | null
    product_text: string | null
    amount: number | null
    tranmode: string | null
    txn_type: string | null
    processor_response_code: string | null
    mapped_clearing_processor: number | null
    group: string
    billing_kind: string | null
    primary_document: TranzilaDocumentSide | null
    cancellation_document: TranzilaDocumentSide | null
}

// Mirrors CACHE_TERMINALS in api/app/Utils/tranzilaDocumentsQuery.js. Wider than the 3-terminal
// TRANZILA_TERMINALS used by the live transactions page: the cache also holds the historical
// mediwho and mediwhotok rows, and an admin browser must be able to see them.
export const DOCUMENT_TERMINALS = ['mediwho', 'mediwhotok', 'mediwho1tok', 'mediwho1', 'mediwhobk']

// Tranzila document action, as STORED on the row: 1 = debit, 3 = credit. Only the stored action
// says what a document is -- which of the two cache columns holds it does not, so the column
// headings stay neutral and the action word is shown inside the cell.
export const DOCUMENT_ACTION_TEXT: { [key: string]: string } = { 1: 'חיוב', 3: 'זיכוי' }

export const documentActionText = action =>
    action === null || action === undefined || action === ''
        ? ''
        : DOCUMENT_ACTION_TEXT[String(action)] || String(action)

// Neutral side terminology. The primary/cancellation split is the cache's document SLOT, not a
// guarantee of invoice-vs-credit, so nothing here implies one.
export const PRIMARY_SIDE_LABEL = 'מסמך ראשי'
export const CANCEL_SIDE_LABEL = 'מסמך ביטול'

export const DOCUMENT_TYPE_OPTIONS = [
    { value: 'all', label: 'הכל' },
    { value: 'primary', label: 'מסמך ראשי בלבד' },
    { value: 'cancellation', label: 'מסמך ביטול בלבד' },
    { value: 'both', label: 'שני המסמכים' },
    { value: 'none', label: 'ללא מסמכים' }
]

// value = mapped_clearing_processor (the STORED generated column the backend filters on).
// raw   = the clearing_processor value CLEARING_TEXT is keyed by, so the Hebrew labels stay
//         single-sourced in ../utils instead of being retyped here.
// 'unmapped' is IS NULL: the bank terminal and the unmapped raw values live there and must
// stay reachable rather than silently disappearing from a cache browser.
export const CLEARING_OPTIONS = [
    { value: '20', label: CLEARING_TEXT['6'] },
    { value: '30', label: CLEARING_TEXT['1'] },
    { value: '40', label: CLEARING_TEXT['2'] },
    { value: 'unmapped', label: 'לא ממופה' }
]

export const clearingText = mapped => {
    if (mapped === null || mapped === undefined || mapped === '') return ''
    const option = CLEARING_OPTIONS.filter(item => item.value === String(mapped))[0]
    return option ? option.label : String(mapped)
}

export const DEFAULT_PER_PAGE = 20

// The cache is ~1M rows and V1 adds no index, so the page is bounded to a recent window both on
// first load AND on Reset -- clearing the window would turn Reset into the most expensive query
// on the page. The operator can still widen or clear the pickers by hand.
// Window edges come from the browser's local calendar; no timezone dependency is introduced.
export const DEFAULT_RANGE_DAYS = 30

export const emptyDocumentFilters = () => ({
    date_from: null,
    date_to: null,
    user_id: '',
    client: '',
    email: '',
    document_number: '',
    transaction_index: '',
    terminal: '',
    // The multi-value filters: a fresh array every call, never a shared constant.
    sku: [],
    // Product IDS (strings), never names and never the SKUs they resolve to: the API owns the
    // products -> subscriptions -> sku mapping, so the URL stays short and the mapping single.
    product_id: [],
    document_type: 'all',
    amount: '',
    clearing_processor: ''
})

// Used for BOTH the initial state and Reset, so the two can never drift apart.
export const initialDocumentFilters = () =>
    Object.assign({}, emptyDocumentFilters(), {
        date_from: moment().subtract(DEFAULT_RANGE_DAYS, 'days'),
        date_to: moment()
    })

const textValue = value => (value === null || value === undefined ? '' : String(value).trim())

// Mirrors MAX_FILTER_LENGTHS.sku and MAX_SKU_VALUES in api/app/Utils/tranzilaDocumentsQuery.js.
// The backend is authoritative -- these exist so the operator is told at the keyboard instead of
// losing a whole search to a 400.
export const SKU_MAX_LENGTH = 6
export const SKU_MAX_VALUES = 50

// Every SKU that reaches state is uppercase. Not a CSS transform: the value itself is folded,
// so the draft, the applied snapshot, the URL and the workbook all carry the same string.
export const normalizeSkuValue = value => textValue(value).toUpperCase()

// Returns a FRESH array plus what had to be refused, so the caller can say why rather than
// silently dropping a tag. Nothing is ever shortened -- an over-long value is rejected whole.
export const normalizeSkuSelection = values => {
    const source = Array.isArray(values) ? values : []
    const normalized = []
    const tooLong = []
    let overflow = false

    source.forEach(entry => {
        const sku = normalizeSkuValue(entry)
        if (!sku) return
        if (sku.length > SKU_MAX_LENGTH) {
            if (tooLong.indexOf(sku) === -1) tooLong.push(sku)
            return
        }
        if (normalized.indexOf(sku) !== -1) return
        if (normalized.length >= SKU_MAX_VALUES) {
            overflow = true
            return
        }
        normalized.push(sku)
    })

    return { values: normalized, tooLong: tooLong, overflow: overflow }
}

// Mirrors MAX_PRODUCT_VALUES in api/app/Utils/tranzilaDocumentsQuery.js.
export const PRODUCT_MAX_VALUES = 50

// The product options as the API sends them -- one per product id, labelled by simplify_name,
// each with its usable subscriptions { id, name, sku } -- de-duplicated by id once more here so
// no response shape can ever put the same product in the dropdown twice.
export const normalizeProductOptions = options => {
    const seen = {}
    const out = []
    for (const option of Array.isArray(options) ? options : []) {
        const id = option && option.id !== undefined && option.id !== null ? String(option.id) : ''
        if (!id || seen[id]) continue
        seen[id] = true
        out.push({
            id: id,
            simplify_name: textValue(option.simplify_name) || `#${id}`,
            active: option.active !== false,
            subscriptions: Array.isArray(option.subscriptions) ? option.subscriptions : []
        })
    }
    return out
}

// The מוצר label is simplify_name, never products.name. Inactive products stay selectable --
// Documents are historical -- and say so. Two DIFFERENT ids that share a simplify_name are
// still two products; only then is the id appended, so the two choices can be told apart.
export const productOptionLabels = options => {
    const count = {}
    for (const option of options || []) count[option.simplify_name] = (count[option.simplify_name] || 0) + 1
    const labels = {}
    for (const option of options || []) {
        const name = count[option.simplify_name] > 1 ? `${option.simplify_name} (#${option.id})` : option.simplify_name
        labels[option.id] = option.active === false ? `${name} — לא פעיל` : name
    }
    return labels
}

// SKU -> the distinct subscription names it belongs to, over the given products (all of them
// when `productIds` is empty). Names are presentation only: they never reach a request.
const skuSubscriptionNames = (productOptions, productIds) => {
    const wanted = Array.isArray(productIds) && productIds.length > 0 ? productIds.map(String) : null
    const names: { [sku: string]: string[] } = {}
    for (const product of productOptions || []) {
        if (wanted && wanted.indexOf(String(product.id)) === -1) continue
        for (const subscription of product.subscriptions || []) {
            const sku = normalizeSkuValue(subscription && subscription.sku)
            if (!sku) continue
            if (!names[sku]) names[sku] = []
            const name = textValue(subscription.name)
            if (name && names[sku].indexOf(name) === -1) names[sku].push(name)
        }
    }
    return names
}

// "SKU — subscription name", several names joined; a SKU with no known subscription stays bare.
export const skuOptionLabel = (sku, names) => (names && names.length ? `${sku} — ${names.join(' / ')}` : sku)

// The מק"ט dropdown, which DEPENDS on the draft מוצר selection:
//   no product selected -> the GLOBAL sku-options list (historical cache SKUs included),
//                          labelled with subscription names wherever the catalog knows them
//   products selected   -> ONLY the SKUs those products map to -- their union, once per SKU
// Each option: { value: the SKU sent to the API, label, search: what typing is matched against }.
export const skuOptionsFor = (globalSkus, productOptions, productIds) => {
    const scoped = Array.isArray(productIds) && productIds.length > 0
    const names = skuSubscriptionNames(productOptions, scoped ? productIds : null)
    const values = []
    const source = scoped ? Object.keys(names) : Array.isArray(globalSkus) ? globalSkus : []
    for (const entry of source) {
        const sku = normalizeSkuValue(entry)
        if (sku && values.indexOf(sku) === -1) values.push(sku)
    }
    return values.sort().map(sku => {
        const skuNames = names[sku] || []
        return {
            value: sku,
            label: skuOptionLabel(sku, skuNames),
            search: [sku]
                .concat(skuNames)
                .join(' ')
                .toLowerCase()
        }
    })
}

// Typing in מק"ט can be a CODE or a subscription NAME. antd's tags mode offers the typed text
// itself as the first choice, so Enter after a name search would otherwise add the name as a
// "SKU". Before any SKU rule runs, a NAME-like entry -- one containing non-ASCII text, which is
// what the Hebrew subscription names are -- is resolved against the current choices: exactly
// one match becomes that SKU; several are refused as ambiguous; none are refused as unmatched.
// Every ASCII entry is left exactly as typed (spaces and commas included), so free entry of
// codes behaves as it always has.
const CODE_LIKE = /^[\x00-\x7F]*$/
export const resolveSkuInput = (values, choices) => {
    const known = (choices || []).map(choice => choice.value)
    const resolved = []
    const ambiguous = []
    const unmatched = []
    for (const value of Array.isArray(values) ? values : []) {
        const raw = textValue(value)
        if (!raw) continue
        if (known.indexOf(raw.toUpperCase()) !== -1 || CODE_LIKE.test(raw)) {
            resolved.push(raw)
            continue
        }
        const needle = raw.toLowerCase()
        const matches = (choices || []).filter(choice => choice.search.indexOf(needle) !== -1)
        if (matches.length === 1) resolved.push(matches[0].value)
        else if (matches.length > 1) ambiguous.push(raw)
        else unmatched.push(raw)
    }
    return { values: resolved, ambiguous: ambiguous, unmatched: unmatched }
}

// A draft SKU selection kept consistent with the draft product selection: with products
// selected, only SKUs that still belong to at least one of them survive. With NO product
// selected the list is global again, so every selected SKU is still a valid exact filter and
// nothing is removed.
export const pruneSkuSelection = (skus, productOptions, productIds) => {
    const selected = Array.isArray(skus) ? skus.slice() : []
    if (!Array.isArray(productIds) || productIds.length === 0) return selected
    const allowed = Object.keys(skuSubscriptionNames(productOptions, productIds))
    return selected.filter(sku => allowed.indexOf(normalizeSkuValue(sku)) !== -1)
}

// Product ids as the API expects them: distinct, non-blank strings, in selection order.
export const normalizeProductSelection = values => {
    const ids = []
    for (const value of Array.isArray(values) ? values : []) {
        const id = textValue(value)
        if (id && ids.indexOf(id) === -1) ids.push(id)
    }
    return { values: ids.slice(0, PRODUCT_MAX_VALUES), overflow: ids.length > PRODUCT_MAX_VALUES }
}

// A filter object whose ARRAY fields are copied too. An outer Object.assign is not enough: the
// draft and the applied snapshot must never share the SKU array, or editing one would silently
// rewrite what the table and the export describe.
export const cloneDocumentFilters = filters => {
    const source = filters || {}
    return Object.assign({}, source, {
        sku: Array.isArray(source.sku) ? source.sku.slice() : source.sku,
        product_id: Array.isArray(source.product_id) ? source.product_id.slice() : source.product_id
    })
}

// FLAT scalar params only. The filterArray / pagination JSON-object contract used elsewhere in
// this admin only works by accident of axios 0.18 stringifying object values; it is not
// repeated here. Empty filters are omitted entirely so the query string stays readable.
//
// THE one place a Documents filter becomes a request parameter. The list adds pagination on top
// of it and the export sends it as-is, so the two can never describe different result sets.
export const buildDocumentFilterPayload = filters => {
    const source = filters || {}
    const payload: any = {}

    if (source.date_from) payload.date_from = moment(source.date_from).format('YYYY-MM-DD')
    if (source.date_to) payload.date_to = moment(source.date_to).format('YYYY-MM-DD')

    const scalars = [
        'user_id',
        'client',
        'email',
        'document_number',
        'transaction_index',
        'terminal',
        'amount',
        'clearing_processor'
    ]
    scalars.forEach(key => {
        const value = textValue(source[key])
        if (value) payload[key] = value
    })

    // sku is a list. Empty means "no SKU filter" and is omitted entirely; a legacy scalar is
    // still accepted here so nothing breaks while state migrates. axios serialises the array as
    // sku[]=A&sku[]=B, which Adonis parses straight back into an array.
    if (Array.isArray(source.sku)) {
        const selection = normalizeSkuSelection(source.sku).values
        if (selection.length > 0) payload.sku = selection
    } else {
        const single = normalizeSkuValue(source.sku)
        if (single) payload.sku = single
    }

    // product_id is a list of ids, sent as product_id[]=12&product_id[]=18 exactly like sku.
    // Empty means "no product filter" and is omitted, so it can never be read as "no products".
    const products = normalizeProductSelection(source.product_id).values
    if (products.length > 0) payload.product_id = products

    // 'all' is the server default; sending it would only add noise to the URL.
    const documentType = textValue(source.document_type)
    if (documentType && documentType !== 'all') payload.document_type = documentType

    return payload
}

// The list = the shared filters + the page window. page/per_page exist ONLY here: the export
// has no pagination and must never send them.
export const buildDocumentsPayload = (filters, page, perPage) =>
    Object.assign({ page: page, per_page: perPage }, buildDocumentFilterPayload(filters))

// Content-Disposition from the server is authoritative; this name is only used when a proxy
// strips the header. It never interpolates a missing or malformed date -- a file called
// "...-undefined_undefined.xlsx" is worse than an undated one.
export const DOCUMENTS_FILE_NAME = 'tranzila-documents.xlsx'

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

export const documentsFileName = payload => {
    const source = payload || {}
    const from = textValue(source.date_from)
    const to = textValue(source.date_to)
    if (!ISO_DATE.test(from) || !ISO_DATE.test(to)) return DOCUMENTS_FILE_NAME
    return `tranzila-documents-${from}_${to}.xlsx`
}

// Mirrors EXPORT_MAX_ROWS / EXPORT_MAX_RANGE_DAYS in api/app/Utils/tranzilaDocumentsQuery.js.
// Only ever used as a fallback when the server's own numbers are missing from an error payload.
export const EXPORT_MAX_ROWS = 10000
export const EXPORT_MAX_RANGE_DAYS = 31

// One Hebrew sentence per structured backend refusal. A raw exception is never shown.
export const documentsExportError = payload => {
    const data = payload || {}
    const code = data.code || data.err_key

    if (code === 'TRANZILA_DOCUMENTS_FILE_TOO_MANY_ROWS') {
        const maxRows = Number(data.max_rows) || EXPORT_MAX_ROWS
        return `ניתן לייצא עד ${maxRows.toLocaleString(
            'he-IL'
        )} רשומות. יש לצמצם את המסננים או את טווח התאריכים ולנסות שוב`
    }

    if (code === 'TRANZILA_DOCUMENTS_FILE_NO_ROWS') return 'לא נמצאו רשומות לייצוא'

    if (code === 'TRANZILA_DOCUMENTS_FILE_INVALID_FILTER') {
        const invalid = Array.isArray(data.invalid_filters) ? data.invalid_filters : []
        const maxDays = Number(data.max_days) || EXPORT_MAX_RANGE_DAYS
        if (invalid.indexOf('date_range_days') !== -1) return `ניתן לייצא טווח של עד ${maxDays} ימים`
        if (invalid.indexOf('date_range') !== -1) return 'תאריך ההתחלה מאוחר מתאריך הסיום'
        if (invalid.indexOf('date_from') !== -1 || invalid.indexOf('date_to') !== -1) {
            return 'יש לבחור טווח תאריכים מלא לייצוא'
        }
        return 'אחד ממסנני החיפוש אינו תקין'
    }

    return 'ייצוא המסמכים לאקסל נכשל'
}

// One transaction row can offer up to two PDFs. A side is ACTIONABLE only when it carries a
// retrieval key: a referenced document the sweep has not resolved yet is still shown in the
// table, it simply has nothing to open. Tooltips stay action-neutral.
export const pdfActionsForRow = row => {
    const actions = []
    const primary = row && row.primary_document
    const cancellation = row && row.cancellation_document

    if (primary && primary.retrieval_key) {
        actions.push({
            key: 'primary',
            retrieval_key: primary.retrieval_key,
            tooltip: 'פתיחת מסמך ראשי',
            icon: 'file-text'
        })
    }
    if (cancellation && cancellation.retrieval_key) {
        actions.push({
            key: 'cancellation',
            retrieval_key: cancellation.retrieval_key,
            tooltip: 'פתיחת מסמך ביטול',
            icon: 'rollback'
        })
    }
    return actions
}

export const pdfActionKey = (row, side) => `${row.id}:${side}`

// Object URLs must outlive the navigation of the reserved tab, so they are revoked on a timer
// rather than synchronously. Conservative on purpose: a slow tab still gets its bytes, and the
// blob is released instead of leaking for the life of the session.
export const OBJECT_URL_TTL_MS = 60000

// --- transaction-details modal (shared with the Transactions page, opened from both tabs)

// The terminals GET /tranzila/transaction-items accepts TODAY -- the API's own live-items allowlist
// (TRANZILA_TRANSACTION_ITEMS_TERMINALS), mirrored here once and shared with the Transactions page,
// so no table or page carries a copy. Every other terminal (mediwho1, mediwhotok, mediwho) would be
// refused with a 400, which the modal would show as a false "could not load" error, so those rows
// open the modal WITHOUT an items request. Widening this list is an API decision.
export const TRANSACTION_ITEMS_TERMINALS = ['mediwho1tok', 'mediwhobk']

// The items request for a detail row, or null when none should be made. Built from the row the
// detail endpoint returned -- its authoritative terminal_name + transaction_index -- never from
// the table row or the current filters.
export const transactionItemsRequest = txn => {
    if (!txn || typeof txn !== 'object') return null
    const terminal = txn.terminal_name
    const transactionId = txn.transaction_index
    if (typeof terminal !== 'string' || TRANSACTION_ITEMS_TERMINALS.indexOf(terminal) === -1) return null
    if (typeof transactionId !== 'string' || !/^[1-9]\d*$/.test(transactionId)) return null
    return { terminal, transaction_id: transactionId }
}
